import React from 'react'

import {
  Box,
  Button,
  Flex,
  InputText,
  RadioSelectionBlock,
  Text,
  styled,
} from '@driftt/dds'
import type { ButtonColorVariantLiteral } from '@driftt/dds'
import { useState } from 'react'
import { useUrlInput } from '../hooks/useUrlInput'
import { FLFormAPIForm, useDetectForm } from 'api/fastlane'

const StyledUL = styled('ul', {
  listStyle: 'initial',
  paddingY: 0,
  paddingX: '$1',
  textStyles: 'metadata',
  color: '$gray12',
  '& > li': {
    width: '50%',
  },
})

export const NONE_OF_ABOVE_FORM = 'NONE_OF_ABOVE'
const FORM_WITHOUT_ID = 'Form without ID'

export const NoneForm: FLFormAPIForm = {
  id: NONE_OF_ABOVE_FORM,
  quickSetupCompatible: false,
  type: 'NONE',
  fields: [],
}

export type FLFormUrlInputProps = {
  onFormUrlSubmit: (formUrl: string) => void
  onFormUrlSuccess: NonNullable<
    Parameters<typeof useDetectForm>[1]
  >['onSuccess']
  onFormSelect: (params: {
    formId?: string
    form?: NonNullable<
      ReturnType<typeof useDetectForm>['data']
    >['data']['forms'][0]
    isManualFL: boolean
    isFormWithoutId: boolean
  }) => void
  defaultFormUrl?: string
  defaultSelectedFormId?: string
  buttonVariant?: ButtonColorVariantLiteral
}

export const FLFormUrlInput = ({
  defaultFormUrl,
  defaultSelectedFormId,
  onFormUrlSubmit,
  onFormUrlSuccess,
  onFormSelect,
  buttonVariant = 'primary',
}: FLFormUrlInputProps) => {
  const [selectedFormId, setSelectedFormId] = useState<string>(
    defaultSelectedFormId ?? ''
  )
  const [formUrl, setFormUrl] = useState<string>(defaultFormUrl ?? '')
  const [draftFormUrl, setDraftFormUrl] = useState<string>(defaultFormUrl ?? '')

  const {
    handleUrlInputBlur,
    handleUrlInputChange,
    error: urlError,
  } = useUrlInput({
    onChange: (newVal) => {
      setDraftFormUrl(newVal)
    },
  })

  const { data: response, isValidating: isDetectFormsLoading } = useDetectForm(
    formUrl ?? '',
    {
      onSuccess: onFormUrlSuccess,
    }
  )

  const forms = response?.data.forms.map((form, i) => ({
    ...form,
    id: form.id ?? FORM_WITHOUT_ID + i,
    type: form.type ?? 'UNKNOWN',
  }))

  const handleSelectFormId = (selectedFormId: string) => {
    setSelectedFormId(selectedFormId)

    const selectedForm =
      selectedFormId !== NONE_OF_ABOVE_FORM
        ? forms?.find((form) => form.id === selectedFormId)
        : NoneForm
    onFormSelect({
      formId: selectedFormId,
      form: selectedForm,
      isManualFL:
        selectedFormId === NONE_OF_ABOVE_FORM ||
        selectedFormId.startsWith(FORM_WITHOUT_ID),
      isFormWithoutId: selectedFormId.startsWith(FORM_WITHOUT_ID),
    })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setDraftFormUrl(draftFormUrl)
    setFormUrl(draftFormUrl)
    onFormUrlSubmit(draftFormUrl)
  }

  return (
    <Flex direction="column" gap={2} fullWidth>
      <Flex fullWidth as="form" onSubmit={handleSubmit}>
        <InputText
          size="full-width"
          label="Form URL"
          hideLabel
          placeholder="https://example.com/contact-us"
          value={draftFormUrl}
          onChange={handleUrlInputChange}
          onBlur={handleUrlInputBlur}
          errorMessage={urlError}
        />
        <Button
          css={{
            whiteSpace: 'nowrap',
          }}
          type="submit"
          disabled={!!urlError || isDetectFormsLoading}
          loading={isDetectFormsLoading}
          variant={buttonVariant}
        >
          Find form
        </Button>
      </Flex>
      {forms && forms?.length > 0 && (
        <>
          {/* Divider */}
          <Box
            fullWidth
            css={{
              borderBottom: '1px solid $gray3',
            }}
          />
          <Text> Please select a form</Text>
          <RadioSelectionBlock.Group
            defaultValue={selectedFormId ?? ''}
            onChange={handleSelectFormId}
          >
            {forms.map(({ id, fields }, index) => (
              <RadioSelectionBlock.Item
                key={id ?? FORM_WITHOUT_ID + index}
                value={id}
                header={id.startsWith(FORM_WITHOUT_ID) ? FORM_WITHOUT_ID : id}
                subheader={`${fields.length} form fields`}
              >
                <StyledUL as={Flex} wrap="wrap" gap={0} fullWidth>
                  {fields
                    .filter(({ hidden }) => !hidden)
                    .map(({ id, label, name }) => (
                      <li key={id}>
                        {label && label.length > 20
                          ? `${label.slice(0, 20)}...`
                          : label ?? name}
                      </li>
                    ))}
                </StyledUL>
              </RadioSelectionBlock.Item>
            ))}
            <RadioSelectionBlock.Item
              value={NONE_OF_ABOVE_FORM}
              header="I don't see the right form"
            />
          </RadioSelectionBlock.Group>
        </>
      )}
    </Flex>
  )
}
